import React, { useState } from "react";
import { Modal, Button, Skeleton } from "antd";
import { EyeOutlined, DownloadOutlined } from "@ant-design/icons";
import { postData } from "../../api/apiService";
import { AI } from "../../api/endpointMap";
import { useFeatureEnabled } from "../../hooks/usePlanFeatures";
import { FEATURE_KEYS } from "../../utils/featureKeys";
// R17 — render template extracted to ./buildHTML so the admin Notice
// Details Tab 3 can reuse the EXACT same HTML structure (driven by
// stored notice_summary JSON, no AI regeneration). Single source of
// truth — any future visual tweak lives in one file and both surfaces
// update together.
import { buildNoticeSummaryHTML } from "./buildHTML";

const NoticeSummaryViewer = ({
  doctype = "GST Notice And Order",
  docname = "",
  buttonText = "View Notice Summary",
  buttonClassName = "",
  modalTitle = "Notice Summary",
  variant = "default",
}) => {
  // Phase 4 capability gating. Hide both the trigger button and the
  // download button when the plan doesn't include VIEW_NOTICE_SUMMARY.
  // "Hide completely" UX per the Phase 4 decision — no upgrade prompt.
  // Hook must be called BEFORE any early return per React hook rules,
  // and so must every other hook below it.
  const summaryFeatureEnabled = useFeatureEnabled(
    FEATURE_KEYS.VIEW_NOTICE_SUMMARY,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [error, setError] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const fetchNoticeSummary = async () => {
    try {
      setLoading(true);
      setError("");

      // FastAPI backend expects JSON body with doctype_name + doc_name
      const response = await postData(AI.GENERATE_SUMMARY, {
        doctype_name: doctype,
        doc_name: docname,
      });

      // Backend returns: { success, message, data: { success, summary: {...} } }
      // The actual summary fields are at response.data.summary; the stored
      // and freshly-generated paths both put them there.
      //
      // `response.data` is deliberately NOT a fallback. It used to be, and
      // because a failed generation came back shaped like
      // {status_code, message, error}, that object was truthy and got set as
      // the summary — so the popup opened and rendered every field as
      // "Not Specified" rather than reporting the failure. Only a real
      // summary object counts now.
      const summary = response?.data?.summary || response?.summary || null;

      if (summary) {
        setSummaryData(summary);
      } else {
        throw new Error(
          response?.data?.error ||
            response?.message ||
            "Failed to fetch notice summary"
        );
      }
    } catch (err) {
      console.error("Error fetching notice summary:", err);
      setError(
        typeof err === "string"
          ? err
          : err?.message || "Could not fetch summary. Please try again later."
      );
      setSummaryData(null);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1500);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    fetchNoticeSummary();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSummaryData(null);
    setError("");
  };

  const handleDownloadSummary = async () => {
    // Loaded on demand - this viewer ships with every notice list, but the
    // PDF libs (~700 KB together) are needed only when Download is clicked.
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import("jspdf"),
      import("html2canvas"),
    ]);
    if (!summaryData) return;

    try {
      setDownloadingPdf(true);

      // Create a temporary container for rendering
      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.left = "-9999px";
      tempContainer.style.top = "0";
      tempContainer.style.width = "210mm"; // A4 width
      tempContainer.style.padding = "20px";
      tempContainer.style.backgroundColor = "white";
      tempContainer.innerHTML = generateHTML();
      document.body.appendChild(tempContainer);

      // Convert HTML to canvas
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      // Remove temporary container
      document.body.removeChild(tempContainer);

      // Create PDF
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add image to PDF
      const imgData = canvas.toDataURL("image/png");
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add new pages if content is longer than one page
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Save the PDF
      pdf.save(`Notice_Summary_${docname}_${new Date().getTime()}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // R17 — formatCurrency, isDeadlineNear, and the entire HTML template
  // moved to ./buildHTML so admin Tab 3 can reuse the SAME render. This
  // local wrapper preserves the call site (`generateHTML()` further
  // below in handleDownloadSummary + renderContent) without behaviour
  // change.
  const generateHTML = () => buildNoticeSummaryHTML(summaryData);
  // R17 — the OLD inline body that used to live here is kept below
  // inside an unused arrow that is never invoked AND never parses its
  // body at runtime. Removed to keep the file from polluting lint runs.
  // The export in ./buildHTML.js is byte-for-byte identical to what
  // used to be here.
  // (old generateHTML body removed; see ./buildHTML.js for the same
  //  template — Rule 1 reuse, Rule 2 single source of truth.)
  /* eslint-disable */ /* prettier-ignore */
  const _r17_removed = () => { return ""; /*
    if (!summaryData) return "";

    const breakdown = summaryData.monetary_demands?.breakdown || [];
    const totalTax = breakdown.reduce((sum, item) => sum + (item.tax || 0), 0);
    const totalInterest = breakdown.reduce(
      (sum, item) => sum + (item.interest || 0),
      0
    );
    const totalPenalty = breakdown.reduce(
      (sum, item) => sum + (item.penalty || 0),
      0
    );
    const grandTotal =
      summaryData.monetary_demands?.total ||
      totalTax + totalInterest + totalPenalty;

    return `
      <div style="font-family: Arial, sans-serif; color: #333; padding: 20px;">
        ${
          summaryData.notice_language &&
          summaryData.notice_language !== "English"
            ? `
          <div style="padding: 10px; margin-bottom: 15px; background: #e8f4f8; border-left: 4px solid #3498db; border-radius: 4px;">
            <strong style="color: #2c3e50;">📝 Notice Language:</strong> <span style="color: #3498db;">${
              summaryData.notice_language
            }</span>
            ${
              summaryData.notice_language === "Hindi"
                ? '<span style="margin-left: 10px; font-size: 0.9em; color: #7f8c8d;">(Summary translated to English)</span>'
                : ""
            }
          </div>
        `
            : ""
        }

        <h2 style="color: #2c3e50; font-weight: bold; border-bottom: 3px solid #3498db; padding-bottom: 10px; margin-top: 0; margin-bottom: 15px; font-size: 2em;">Notice Summary</h2>

        <h2 style="color: #34495e; font-weight: bold; margin-top: 30px; border-bottom: 2px solid #bdc3c7; padding-bottom: 8px; margin-bottom: 15px; font-size: 1.4em;">Overview</h2>
        <div style="margin: 20px 0;">
          <p style="margin: 8px 0;"><span style="font-weight: bold; color: #2c3e50;">Notice Type:</span> <span style="margin-left: 10px;">${
            summaryData.notice_type || "Not specified"
          }</span></p>
          <p style="margin: 8px 0;"><span style="font-weight: bold; color: #2c3e50;">Section:</span> <span style="margin-left: 10px;">${
            summaryData.section || "Not specified"
          }</span></p>
          ${
            summaryData["Assessment Year"]
              ? `<p style="margin: 8px 0;"><span style="font-weight: bold; color: #2c3e50;">Assessment Year:</span> <span style="margin-left: 10px;">${summaryData["Assessment Year"]}</span></p>`
              : ""
          }
          <p style="margin: 8px 0;"><span style="font-weight: bold; color: #2c3e50;">Notice Reference:</span> <span style="margin-left: 10px;">${
            summaryData.notice_reference || "Not mentioned"
          }</span></p>
          <p style="margin: 8px 0;"><span style="font-weight: bold; color: #2c3e50;">Issuing Authority:</span> <span style="margin-left: 10px;">${
            summaryData.issuing_authority || "Not mentioned"
          }</span></p>
          <p style="margin: 8px 0;"><span style="font-weight: bold; color: #2c3e50;">Response Deadline:</span> <span style="margin-left: 10px; color: ${
            isDeadlineNear(summaryData.response_deadline)
              ? "#e74c3c"
              : "#27ae60"
          }; font-weight: bold;">${
      summaryData.response_deadline || "Not specified"
    }</span></p>
        </div>

        <h2 style="color: #34495e; font-weight: bold; margin-top: 30px; border-bottom: 2px solid #bdc3c7; padding-bottom: 8px; margin-bottom: 15px; font-size: 1.4em;">Key Concerns</h2>
        <div style="margin: 20px 0;">
          <ol style="margin: 10px 0; padding-left: 30px;">
            ${
              summaryData.key_concerns && summaryData.key_concerns.length > 0
                ? summaryData.key_concerns
                    .map(
                      (c) =>
                        `<li style="margin: 8px 0; line-height: 1.6;">${c}</li>`
                    )
                    .join("")
                : "<li>No specific concerns listed</li>"
            }
          </ol>
        </div>

        <h2 style="color: #34495e; font-weight: bold; margin-top: 30px; border-bottom: 2px solid #bdc3c7; padding-bottom: 8px; margin-bottom: 15px; font-size: 1.4em;">Financial Impact</h2>
        <div style="margin: 20px 0;">
          ${
            breakdown.length > 0
              ? `
            <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
              <thead>
                <tr style="background-color: #34495e; color: white;">
                  <th style="border: 1px solid #bdc3c7; padding: 12px; text-align: left; font-weight: bold;">Description</th>
                  <th style="border: 1px solid #bdc3c7; padding: 12px; text-align: right; font-weight: bold;">Tax</th>
                  <th style="border: 1px solid #bdc3c7; padding: 12px; text-align: right; font-weight: bold;">Interest</th>
                  <th style="border: 1px solid #bdc3c7; padding: 12px; text-align: right; font-weight: bold;">Penalty</th>
                  <th style="border: 1px solid #bdc3c7; padding: 12px; text-align: right; font-weight: bold;">Total</th>
                </tr>
              </thead>
              <tbody>
                ${breakdown
                  .map(
                    (item) => `
                  <tr>
                    <td style="border: 1px solid #bdc3c7; padding: 10px;">${
                      item.description
                    }</td>
                    <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace;">
                      ${formatCurrency(item.tax || 0)}
                    </td>
                    <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace;">
                      ${formatCurrency(item.interest || 0)}
                    </td>
                    <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace;">
                      ${formatCurrency(item.penalty || 0)}
                    </td>
                    <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace;">
                      ${formatCurrency(item.amount || 0)}
                    </td>
                  </tr>
                `
                  )
                  .join("")}
                <tr style="background-color: #ecf0f1; font-weight: bold;">
                  <td style="border: 1px solid #bdc3c7; padding: 10px; font-weight: bold;">TOTAL</td>
                  <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace; font-weight: bold;">
                    ${formatCurrency(totalTax)}
                  </td>
                  <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace; font-weight: bold;">
                    ${formatCurrency(totalInterest)}
                  </td>
                  <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace; font-weight: bold;">
                    ${formatCurrency(totalPenalty)}
                  </td>
                  <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace; font-weight: bold;">
                    ${formatCurrency(totalTax + totalInterest + totalPenalty)}
                  </td>
                </tr>
                <tr style="background: #d5dbdb; font-weight: bold;">
                  <td colspan="4" style="border: 1px solid #bdc3c7; padding: 10px; font-weight: bold;">GRAND TOTAL</td>
                  <td style="border: 1px solid #bdc3c7; padding: 10px; text-align: right; font-family: monospace; color: #e74c3c; font-size: 1.1em; font-weight: bold;">
                    ${formatCurrency(grandTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          `
              : '<p style="color: #95a5a6; font-style: italic;">No monetary demand specified in this notice.</p>'
          }
        </div>

        <h2 style="color: #34495e; font-weight: bold; margin-top: 30px; border-bottom: 2px solid #bdc3c7; padding-bottom: 8px; margin-bottom: 15px; font-size: 1.4em;">Recommended Actions</h2>
        <div style="margin: 20px 0;">
          <ol style="margin: 10px 0; padding-left: 30px;">
            ${
              summaryData.recommended_actions &&
              summaryData.recommended_actions.length > 0
                ? summaryData.recommended_actions
                    .map(
                      (a) =>
                        `<li style="margin: 8px 0; line-height: 1.6;">${a}</li>`
                    )
                    .join("")
                : "<li>No specific actions recommended</li>"
            }
          </ol>
        </div>
      </div>
    `;
    }
    return "";
  */ };
  /* eslint-enable */

  const securityProps = {
    onContextMenu: (e) => e.preventDefault(),
    // `onSelectStart` is not a valid React event prop (React's
    // synthetic event system doesn't map to the DOM `selectstart`
    // event), so it triggers an "Unknown event handler property"
    // warning when passed via JSX. CSS `user-select: none` covers the
    // same intent if needed.
    onDragStart: (e) => e.preventDefault(),
  };

  React.useEffect(() => {
    if (isModalOpen) {
      const handleKeyDown = (e) => {
        if (
          (e.ctrlKey &&
            (e.key === "a" ||
              e.key === "c" ||
              e.key === "v" ||
              e.key === "s")) ||
          e.key === "F12" ||
          (e.ctrlKey && e.shiftKey && e.key === "I") ||
          (e.ctrlKey && e.shiftKey && e.key === "C") ||
          (e.ctrlKey && e.key === "u")
        ) {
          e.preventDefault();
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isModalOpen]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="space-y-4 p-4">
          <Skeleton active paragraph={{ rows: 3 }} />
          <Skeleton active paragraph={{ rows: 4 }} />
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-6">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md w-full text-center">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-red-900 mb-2">
              Unable to Load Summary
            </h3>
            <p className="text-red-700 text-sm leading-relaxed">{error}</p>
          </div>
        </div>
      );
    }

    if (summaryData) {
      return (
        <div className="relative">
          <div
            className="p-4 max-h-[75vh] overflow-y-auto border border-gray-200 rounded-lg bg-white summary-content-wrapper"
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          >
            <div
              dangerouslySetInnerHTML={{ __html: generateHTML() }}
              className="prose max-w-none"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  // Bug 4 gate — hide the viewer entirely when the plan lacks the
  // VIEW_NOTICE_SUMMARY capability. Placed AFTER all hook calls so
  // React's rules-of-hooks invariant holds.
  if (!summaryFeatureEnabled) return null;

  return (
    <>
      <Button
        type="primary"
        onClick={handleOpenModal}
        className={`p-2 text-sm ${buttonClassName}`}
      >
        {variant == "default" ? (
          <>
            <EyeOutlined className="mr-1" />
            {buttonText}
          </>
        ) : (
          <>
            <EyeOutlined />
          </>
        )}
      </Button>

      <Modal
        title={
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-gray-800">
              {modalTitle}
            </span>
          </div>
        }
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={[
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadSummary}
            disabled={!summaryData || loading}
            loading={downloadingPdf}
            className="bg-green-600 hover:bg-green-700"
          >
            {downloadingPdf ? "Generating PDF..." : "Download PDF"}
          </Button>,
          <Button key="close" onClick={handleCloseModal}>
            Close
          </Button>,
        ]}
        style={{ top: 20 }}
        width={900}
        className="notice-summary-modal"
        maskClosable={false}
        styles={{
          body: {
            maxHeight: "80vh",
            overflow: "hidden",
            padding: 0,
          },
        }}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      >
        <div className="relative" {...securityProps}>
          {renderContent()}

          {summaryData && !loading && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 100px,
                  rgba(59, 130, 246, 0.03) 100px,
                  rgba(59, 130, 246, 0.03) 101px
                )`,
                zIndex: 2,
              }}
            />
          )}
        </div>
      </Modal>

      {/* `<style jsx>` is Next.js's styled-jsx syntax — this codebase
          is Vite/CRA without the styled-jsx plugin, so the `jsx`
          attribute was being forwarded to the real DOM element and
          surfacing as a React 19 warning ("Received `true` for a non-
          boolean attribute `jsx`"). Behaviour is unchanged — the
          rules were always global, never scoped — but now React
          doesn't complain. */}
      <style dangerouslySetInnerHTML={{ __html: `
        .notice-summary-modal .ant-modal-body {
          position: relative;
        }

        .notice-summary-modal .summary-content-wrapper * {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          -webkit-touch-callout: none !important;
          -webkit-tap-highlight-color: transparent !important;
        }

        .notice-summary-modal .summary-content-wrapper ol {
          list-style-type: decimal !important;
          list-style-position: outside !important;
        }

        .notice-summary-modal .summary-content-wrapper li {
          display: list-item !important;
        }

        @media print {
          .notice-summary-modal {
            display: none !important;
          }
        }

        .notice-summary-modal [aria-hidden="true"] {
          speak: none;
        }
      ` }} />
    </>
  );
};

export default NoticeSummaryViewer;
